import { createContext, useReducer, useEffect } from "react";
import notificationReducer from "./NotificationReducer";
import { getTopTenNotifications } from './NotificationActions';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import notificationSound from '../../utils/NotificationSound';

const NotificationContext = createContext();

export const NotificationProvider = ({children}) => {
    const initialState = {
        notifications: [],
        notification: {},
        loading: false,
    }

    const [state, dispatch] = useReducer(notificationReducer, initialState);

    useEffect(() => {
        // Preload notification sound for better performance
        notificationSound.preloadSound();
        
        // Fetch initial notifications when provider mounts
        const fetchInitialNotifications = async () => {
            try {
                dispatch({ type: 'SET_LOADING' });
                console.log('NotificationProvider: Fetching initial notifications...');
                const response = await getTopTenNotifications();
                
                if (response && !response.error && response.data) {
                    console.log('NotificationProvider: Initial notifications fetched:', response.data);
                    dispatch({ 
                        type: 'GET_NOTIFICATIONS', 
                        payload: Array.isArray(response.data) ? response.data : [] 
                    });
                } else {
                    console.error('NotificationProvider: Failed to fetch initial notifications:', response?.error);
                    dispatch({ type: 'GET_NOTIFICATIONS', payload: [] });
                }
            } catch (error) {
                console.error('NotificationProvider: Error fetching initial notifications:', error);
                dispatch({ type: 'GET_NOTIFICATIONS', payload: [] });
            }
        };

        // Setup WebSocket connection that stays active
        const setupWebSocket = () => {
            const WS_BASE = process.env.REACT_APP_WS_BASE || 'http://localhost:8080/api/';
            const LOCAL_STORAGE_KEY = 'halg-service-center.auth.user';
            
            console.log('NotificationProvider: Setting up WebSocket connection...');
            
            const socket = new SockJS(`${WS_BASE}ws`);
            const client = new Client({
                webSocketFactory: () => socket,
                reconnectDelay: 5000,
                onConnect: () => {
                    console.log('NotificationProvider: STOMP connected successfully!');

                    // Subscribe to all notification channels
                    client.subscribe('/topic/notifications', (message) => {
                        try {
                            const body = JSON.parse(message.body);
                            dispatch({ type: 'ADD_NOTIFICATION', payload: body });
                            console.log('NotificationProvider: Received notification:', body);
                            
                            // Play notification sound
                            notificationSound.playNotificationSound();
                        } catch (err) {
                            console.error('NotificationProvider: Failed to parse notification', err, message.body);
                        }
                    });

                    client.subscribe('/user/queue/notifications', (message) => {
                        try {
                            const body = JSON.parse(message.body);
                            dispatch({ type: 'ADD_NOTIFICATION', payload: body });
                            
                            // Play notification sound
                            notificationSound.playNotificationSound();
                        } catch (err) {
                            console.error('NotificationProvider: Failed to parse private notification', err);
                        }
                    });


                    // Get facility ID from local storage if available
                    try {
                        const userStr = localStorage.getItem(LOCAL_STORAGE_KEY);
                        const user = userStr ? JSON.parse(userStr) : null;
                        const facilityId = user?.facilityId;

                        if (facilityId) {
                            client.subscribe(`/topic/facility/${facilityId}`, (message) => {
                                try {
                                    const body = JSON.parse(message.body);
                                    dispatch({ type: 'ADD_NOTIFICATION', payload: body });
                                    
                                    // Play notification sound
                                    notificationSound.playNotificationSound();
                                } catch (err) {
                                    console.error('NotificationProvider: Failed to parse facility notification', err);
                                }
                            });


                            //Remove notification when it's been read
                            client.subscribe(`/topic/facility/${facilityId}/read-notification`, (message) => {
                                try {
                                    const body = JSON.parse(message.body);
                                    dispatch({ type: 'REMOVE_NOTIFICATION', payload: body });
                            
                                } catch (err) {
                                    console.error('NotificationProvider: Failed to parse private notification', err);
                                }
                            });
                        }
                    } catch (err) {
                        console.error('NotificationProvider: Error reading user from localStorage', err);
                    }
                },
                onDisconnect: () => {
                    console.log('NotificationProvider: STOMP disconnected');
                },
                onWebSocketError: (error) => {
                    console.error('NotificationProvider: WebSocket error:', error);
                },
                onStompError: (frame) => {
                    console.error('NotificationProvider: STOMP error:', frame.headers['message']);
                },
                debug: (str) => {
                    console.debug('NotificationProvider: STOMP Debug:', str);
                },
            });

            client.activate();
            return client;
        };

        // Initialize
        fetchInitialNotifications();
        const wsClient = setupWebSocket();

        // Cleanup on unmount
        return () => {
            console.log('NotificationProvider: Cleaning up WebSocket connection...');
            wsClient?.deactivate();
        };
    }, []);

    return <NotificationContext.Provider value = {{
        ...state,
        dispatch,
        // Expose sound control functions
        toggleNotificationSound: (enabled) => notificationSound.setEnabled(enabled),
        setNotificationVolume: (volume) => notificationSound.setVolume(volume),
        testNotificationSound: () => notificationSound.playNotificationSound(),
    }}>
        {children}
    </NotificationContext.Provider>
}

export default NotificationContext;